import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Helpers from '../../helpers';
import Config from '../../config';
import {
    Switch,
    FormHelperText,
    Chip,
    Grid,
    Card,
    Toolbar,
    CardContent,
    TextField,
    Button,
    Dialog,
    DialogContent,
    Typography,
    DialogActions,
    Select,
    MenuItem,
    AccordionSummary,
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Timeline, TimelineEvent } from 'react-event-timeline';
import LogIcon from '@mui/icons-material/Check';
import Moment from 'react-moment';
import ExtendedField from '../../components/text_field';
import ConfirmDialog from '../../components/confirm_dialog';
import { Form, Field } from 'react-final-form';
import DownloadButton from '../../components/download_button';
import moment from 'moment';
import ExtendedSelectBase from '../../components/select';
import {PriorityChip} from './onboarding';
import { RetailerOnboarding, loadPartnerDetails, PartnerOnboardingTasks } from './supplier_onboarding';
import isEqual from 'lodash.isequal';
import ContentLoader from '../../components/content_loader';
import { ExpandMore } from '@mui/icons-material';
import {
    IntegrationDetailModel,
    IntegrationLog,
    OnboardingCustomFieldConfig,
    TestCaseGroupsConfig,
} from './onboarding_details.types';

export type { IntegrationDetailModel };

// ExtendedSelect is a JS component wrapped in forwardRef; its inferred type
// only exposes RefAttributes, so cast to ComponentType<any> to allow the
// variant/options/onChange/style props (same pattern as supplier_onboarding.tsx).
const ExtendedSelect = ExtendedSelectBase as React.ComponentType<any>;

const StyledLog = styled('div')({
    "& p": {
        margin: 0
    },
    fontWeight: 400
});

interface LogProps {
    log: IntegrationLog;
    contentStyle?: React.CSSProperties;
}

const Log = (props: LogProps) => {
    let {log, contentStyle} = props;
    return <TimelineEvent
      title={<>{log.Message}</>}
      contentStyle={contentStyle || {boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)', borderRadius: '4px'}}
      createdAt={<><b>{log.Username}</b> on <Moment format='MM/DD/YYYY hh:mm A'>{log.Date}</Moment></>}
      icon={<LogIcon style={{fontSize: '20px'}}/>}
      iconColor="#737373"
    >
      <StyledLog/>
    </TimelineEvent>;
};

interface WrappedFieldProps {
    label: React.ReactNode;
    children?: React.ReactNode;
    innerStyle?: React.CSSProperties;
    labelStyle?: React.CSSProperties;
}

const WrappedField = (props: WrappedFieldProps) => {
    let {label, children, innerStyle, labelStyle} = props;
    return <div style={{display: "flex", minHeight: "2.5em"}}>
        <div style={{width: "20em", display: "inline-block", fontWeight: "bold", paddingTop: "4px", ...labelStyle}}>{label}</div>
        <div style={{display: "inline-flex", flex: 1, ...innerStyle}}>{children}</div>
    </div>;
};

const Accordion = styled(MuiAccordion)(({ theme }) => ({
    "&:before": {
        display: 'none'
    }
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    paddingRight: 0,
    paddingLeft: 0
}));

interface OnboardingDetailsProps {
    setTitle?: (title: string, includeInRecents?: boolean) => void;
    isRetailer?: boolean;
    history?: any;
    location?: any;
    match?: any;
}

interface OnboardingDetailsState {
    showConfirm: boolean;
    showError: boolean;
    showReject: boolean;
    showRejectGoLive: boolean;
    tags: { label: string; value: string }[];
    customFields: OnboardingCustomFieldConfig[];
    testCaseGroups: TestCaseGroupsConfig | Record<string, never>;
    integration?: IntegrationDetailModel | null;
    tasks?: PartnerOnboardingTasks | null;
    error?: string;
    confirmMessage?: string;
    onConfirm?: () => void;
    rejectMessage?: string;
}

const OnboardingDetails = (props: OnboardingDetailsProps) => {
    const [state, setState] = useState<OnboardingDetailsState>({showConfirm: false, showError: false, showReject: false, showRejectGoLive: false, tags: [], customFields: [], testCaseGroups: {}});
    // Preserve class setState partial-merge semantics: every call below merges
    // a subset of fields into the previous state rather than replacing it.
    const mergeState = useCallback((partial: Partial<OnboardingDetailsState>) => {
        setState(prev => ({...prev, ...partial}));
    }, []);

    const loadIntegration = async () => {
        try{
            let integrationId = new Helpers().queryString()["id"];
            let integration: IntegrationDetailModel = (await axios.get(Config.oauth + '/integration/getintegration?id=' + integrationId)).data;
            if(integration.Logs){
                integration.Logs = integration.Logs.sort((a, b) => (moment(b.Date).valueOf() - moment(a.Date).valueOf()));
            }
            integration.StatusColor = "#ffa500";
            if(integration.IsLive){
                integration.StatusColor = "#448044";
            } else if (integration.Integration.Status === "New"){
                integration.StatusColor = "#275E7A";
            }
            if(integration.Integration.RequestedLiveDate){
                integration.Integration.RequestedLiveDate = moment(integration.Integration.RequestedLiveDate).utc().format('YYYY-MM-DD');
            }
            if(integration.Integration.Tags){
                integration.Integration.Tags = (integration.Integration.Tags as string).split("|").filter(r => !(!r));
            }
            integration.AccountType = "New";
            if(integration.Integration.ExistingAccount){
                integration.AccountType = "Existing";
            }
            if(props.setTitle && integration.Integration && integration.Integration.CompanyName) {
                props.setTitle(`Onb: ${integration.Integration.CompanyName}`, true);
            }
            let tasks = await loadPartnerDetails(integration.Integration);
            mergeState({integration, tasks});
        }catch(e){
            mergeState({integration: null, tasks: null});
        }
    };

    useEffect(() => {
        (async () => {
            let config = (await axios.get(Config.oauth + '/integration/getconfig')).data;
            let tags = config.Tags || [];
            let testCaseGroups: TestCaseGroupsConfig | Record<string, never> = {};
            if(!Config.production){
                testCaseGroups = (await axios.get(Config.api + '/odata/Company/Functions.GetTestCaseGroups')).data;
            }
            mergeState({tags: tags.map((r: string) => ({label: r, value: r})), customFields: config.CustomFields || [], testCaseGroups});
            await loadIntegration();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateIntegration = async (url: string, data: any) => {
        try{
            await axios.post(Config.oauth + url + state.integration!.Integration.Id, data);
            loadIntegration();
        }catch(err: any){
            let error = 'Unexpected error.';
            if(err.response && err.response.data && err.response.data.error){
                error = err.response.data.error;
            }
            mergeState({showError: true, error});
        }
    };
    const setPriority = async (e: any) => {
        let data = {Priority: e.target.value};
        if(data.Priority === state.integration!.Integration.Priority){
            return;
        }
        await updateIntegration('/integration/OperatorUpdatePriority/', data);
    };
    const setTags = async (e: any) => {
        let data = {Tags: e};
        // Pre-existing bug (predates this migration, confirmed via git history):
        // isEqual() was called with a single argument (the result of `===`
        // instead of a comma-separated second argument), so it always compared
        // a boolean against undefined and never matched. That defeated the
        // no-op guard, causing every tag interaction to POST a redundant
        // update. v2's equivalent (onboarding_details_v2.tsx) already has the
        // correct two-argument form; matching it here.
        if(isEqual(data.Tags, state.integration!.Integration.Tags)){
            return;
        }
        await updateIntegration('/integration/OperatorUpdateTags/', data);
    };
    const setReminders = async (e: any) => {
        let data = {DisableReminders: e.target.checked};
        if(data.DisableReminders === state.integration!.Integration.DisableReminders){
            return;
        }
        await updateIntegration('/integration/OperatorUpdateReminder/', data);
    };
    const setIntegrationType = async (e: any) => {
        let data = {IntegrationType: e.target.value};
        if(data.IntegrationType === state.integration!.Integration.IntegrationType){
            return;
        }
        await updateIntegration('/integration/OperatorUpdateType/', data);
    };
    const setGoLiveDate = async (e: any) => {
        let data = {RequestedLiveDate: moment(e.target.value).format('YYYY-MM-DD')};
        if(data.RequestedLiveDate === state.integration!.Integration.RequestedLiveDate){
            return;
        }
        await updateIntegration('/integration/OperatorUpdateRequestedLiveDate/', data);
    };
    const setTestGroup = async (e: any) => {
        let data = {TestGroup: e.target.value};
        if(data.TestGroup === state.integration!.Integration.TestGroup){
            return;
        }
        await updateIntegration('/integration/OperatorUpdateTestGroup/', data);
        if(state.integration!.Integration.CoId){
            let url = Config.api + `/odata/Company/Functions.SetSystemSettings?system=Testing&partnerId=${state.integration!.Integration.CoId}`;
            await axios.put(url, {TestCaseGroup: e.target.value});
        }
    };
    const reject = async (e?: any) => {
        if(e){
            try{
                await axios.post(Config.oauth + '/integration/operatorreject/' + state.integration!.Integration.Id, e);
                mergeState({showReject: false});
                loadIntegration();
            }catch(err: any){
                let error = 'Unexpected error.';
                if(err.response && err.response.data && err.response.data.error){
                    error = err.response.data.error;
                }
                mergeState({showError: true, error});
            }
        } else {
            mergeState({showReject: true, rejectMessage: ''});
        }
    };
    const rejectGoLive = async (e?: any) => {
        if(e){
            try{
                await axios.post(Config.oauth + '/integration/operatorrejectgolive/' + state.integration!.Integration.Id, e);
                mergeState({showRejectGoLive: false});
                loadIntegration();
            }catch(err: any){
                let error = 'Unexpected error.';
                if(err.response && err.response.data && err.response.data.error){
                    error = err.response.data.error;
                }
                mergeState({showError: true, error});
            }
        } else {
            mergeState({showRejectGoLive: true, rejectMessage: ''});
        }
    };
    const accept = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/operatoraccept/' + state.integration!.Integration.Id);
                mergeState({showConfirm: false});
                loadIntegration();
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                mergeState({showConfirm: false, showError: true, error});
            }
        } else {
            mergeState({showConfirm: true, confirmMessage: 'Are you sure you want to accept this integration request?', onConfirm: () => accept(true)});
        }
    };
    const retry = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/retry/' + state.integration!.Integration.Id);
                mergeState({showConfirm: false});
                loadIntegration();
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                mergeState({showConfirm: false, showError: true, error});
            }
        } else {
            mergeState({showConfirm: true, confirmMessage: 'Are you sure you want to retry the last action?', onConfirm: () => retry(true)});
        }
    };
    const resendInvite = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/resendinvite/' + state.integration!.Integration.Id);
                mergeState({showConfirm: false});
                loadIntegration();
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                mergeState({showConfirm: false, showError: true, error});
            }
        } else {
            mergeState({showConfirm: true, confirmMessage: 'Are you sure you want to resend a portal invitation to the primary contact?', onConfirm: () => resendInvite(true)});
        }
    };
    const resendFirstEmail = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/ResendInitialEmail/' + state.integration!.Integration.Id);
                mergeState({showConfirm: false});
                loadIntegration();
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                mergeState({showConfirm: false, showError: true, error});
            }
        } else {
            mergeState({showConfirm: true, confirmMessage: 'Are you sure you want to resend the onboarding invitation to the primary contact?', onConfirm: () => resendFirstEmail(true)});
        }
    };
    const sendOnboardingReminder = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/SendOnboardingReminder/' + state.integration!.Integration.Id);
                mergeState({showConfirm: false});
                loadIntegration();
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                mergeState({showConfirm: false, showError: true, error});
            }
        } else {
            mergeState({showConfirm: true, confirmMessage: 'Are you sure you want to send an onboarding reminder to the primary contact?', onConfirm: () => sendOnboardingReminder(true)});
        }
    };
    const goLive = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/golive/' + state.integration!.Integration.Id);
                mergeState({showConfirm: false});
                loadIntegration();
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                mergeState({showConfirm: false, showError: true, error});
            }
        } else {
            mergeState({showConfirm: true, confirmMessage: 'Are you sure you want to move this integration into production?', onConfirm: () => goLive(true)});
        }
    };

    let model = state.integration;
    let tasks = state.tasks;
    if(model){
            return <><Grid container spacing={2}>
                <Grid size={12}>
                    <Card>
                        <Toolbar className='lbdocbar' style={{display: 'flex'}}>
                            <div style={{flex: 1, textAlign: 'left'}}>Integration: {model.Integration.CompanyName}</div>
                            <div className='noPrint' style={{display: 'flex'}}>
                                {model.CanOperatorAccept && <Button color='primary' onClick={() =>accept()}>Accept</Button>}
                                {model.CanOperatorAccept && <Button color='primary' onClick={() =>reject()}>Reject</Button>}
                                {model.CanRejectGoLive && <Button color='primary' onClick={() =>rejectGoLive()}>Reject Request to go Live</Button>}
                                {model.Integration.Error && <Button color='primary' onClick={() =>retry()}>Retry</Button>}
                                {!model.Integration.Error && model.CanResendFirstEmail && <Button color='primary' onClick={() =>resendFirstEmail()}>Resend Onboarding Invite</Button>}
                                {!model.Integration.Error && model.CanSendOnboardingReminder && <Button color='primary' onClick={() =>sendOnboardingReminder()}>Send Onboarding Reminder</Button>}
                                {!model.Integration.Error && model.CanResendInvite && <Button color='primary' onClick={() =>resendInvite()}>Resend User Invite</Button>}
                                {!model.Integration.Error && model.CanGoLive && <Button color='primary' onClick={() =>goLive()}>Push Supplier Live</Button>}
                            </div>
                        </Toolbar>
                        <Toolbar className='lbstatusbar' style={{backgroundColor: model.StatusColor}}>Status: {model.Integration.Status}<span style={{right: '24px', position: 'absolute'}}>Started: <Moment format='MM/DD/YYYY h:mm A'>{model.Integration.StartDate}</Moment> </span></Toolbar>
                        {model.Integration.Error ? <Toolbar className='lbstatusbar' style={{backgroundColor: "#EE3224"}}>Error: {model.Integration.ErrorMessage}</Toolbar> : ''}

                    </Card>
                </Grid>
                <Grid size={6}>
                    <Card>
                    <Toolbar className='lbtoolbar'>Onboarding Details</Toolbar>
                    <CardContent style={{textAlign: 'left', display: 'flex', flexDirection: 'column'}}>
                    <Accordion style={{boxShadow: 'none', marginTop: '1em'}} expanded>
                        <AccordionSummary id="postPanel"><Typography variant='h6' style={{paddingBottom: '0.5em'}}>General</Typography></AccordionSummary>
                        <AccordionDetails style={{flexDirection: 'column', textAlign: 'left'}}>
                            <WrappedField label="Company name">
                                <TextField style={{flex: 1}} disabled value={model.Integration.CompanyName || ''}/>
                            </WrappedField>
                            <WrappedField label="Logicbroker account number">
                                <TextField style={{flex: 1}} disabled value={model.Integration.CoId || ''}/>
                            </WrappedField>
                            <WrappedField label="Connection type">
                                <div>
                                    <Select style={{width: '12em'}} name="IntegrationType" value={model.Integration.IntegrationType || ''} onChange={setIntegrationType}>
                                        {(model.AllowedTypes || []).map(r => <MenuItem value={r} key={r}>{r}</MenuItem>)}
                                    </Select>
                                    <FormHelperText>Changing connection type will not change settings within Logicbroker after the company has been deployed to stage.</FormHelperText>
                                </div>
                            </WrappedField>
                            {(state.testCaseGroups as TestCaseGroupsConfig).Enabled && <WrappedField label="Test group">
                                <Select style={{width: '12em', paddingBottom: '0.5em'}} name="TestGroup" value={model.Integration.TestGroup || ''} onChange={setTestGroup}>
                                    <MenuItem value="">None</MenuItem>
                                    {(state.testCaseGroups as TestCaseGroupsConfig).Groups!.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                </Select>
                            </WrappedField>}
                            <WrappedField label="Priority">
                                <Select style={{width: '12em', paddingBottom: '0.5em'}} name="Priority" value={model.Integration.Priority || ''} onChange={setPriority}>
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="Ignored"><PriorityChip size="small" value="Ignored"/></MenuItem>
                                    <MenuItem value="Low"><PriorityChip size="small" value="Low"/></MenuItem>
                                    <MenuItem value="Medium"><PriorityChip size="small" value="Medium"/></MenuItem>
                                    <MenuItem value="High"><PriorityChip size="small" value="High"/></MenuItem>
                                </Select>
                            </WrappedField>
                            {!Config.production && <WrappedField label="Expected live date">
                                <TextField style={{width: '12em'}} type="date" disabled={model.IsLive} value={model.Integration.RequestedLiveDate || ''} onChange={setGoLiveDate}/>
                            </WrappedField>}
                            <WrappedField label="Tags">
                                <ExtendedSelect style={{flex: 1}} placeholder='Select tags...' value={model.Integration.Tags || []} options={state.tags} onChange={setTags}/>
                            </WrappedField>
                            <WrappedField label="Disable Automated Reminders" labelStyle={{alignContent: 'center'}}>
                                <Switch style={{flex: 1}} checked={!(!model.Integration.DisableReminders)} onChange={setReminders}/>
                            </WrappedField>
                            </AccordionDetails>
                        </Accordion>
                        <Divider/>
                        <Accordion style={{boxShadow: 'none', marginTop: '1em'}} defaultExpanded>
                            <AccordionSummary id="postPanel" expandIcon={<ExpandMore style={{color: 'black'}}/>}><Typography variant='h6' style={{paddingBottom: '0.5em'}}>Onboarding form</Typography></AccordionSummary>
                            <AccordionDetails style={{flexDirection: 'column', textAlign: 'left'}}>
                                <WrappedField label="Company name">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.CompanyName || ''}/>
                                </WrappedField>
                                <WrappedField label="Account type">
                                    <TextField style={{flex: 1}} disabled value={model.AccountType || ''}/>
                                </WrappedField>
                                <WrappedField label="Primary contact first name">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.FirstName || ''}/>
                                </WrappedField>
                                <WrappedField label="Primary contact last name">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.LastName || ''}/>
                                </WrappedField>
                                <WrappedField label="Primary contact email address">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.Email || ''}/>
                                </WrappedField>
                                <WrappedField label="Address line 1">
                                    <TextField style={{flex: 1}} disabled value={(model.CompanyAddress || {}).Address1 || ''}/>
                                </WrappedField>
                                <WrappedField label="Address line 2">
                                    <TextField style={{flex: 1}} disabled value={(model.CompanyAddress || {}).Address2 || ''}/>
                                </WrappedField>
                                <WrappedField label="City or town">
                                    <TextField style={{flex: 1}} disabled value={(model.CompanyAddress || {}).City || ''}/>
                                </WrappedField>
                                <WrappedField label="State">
                                    <TextField style={{flex: 1}} disabled value={(model.CompanyAddress || {}).State || ''}/>
                                </WrappedField>
                                <WrappedField label="Postal code">
                                    <TextField style={{flex: 1}} disabled value={(model.CompanyAddress || {}).Zip || ''}/>
                                </WrappedField>
                                <WrappedField label="Country">
                                    <TextField style={{flex: 1}} disabled value={(model.CompanyAddress || {}).Country || ''}/>
                                </WrappedField>
                                <WrappedField label="Merchandiser name">
                                    <TextField style={{flex: 1}} disabled value={(model.Integration.MerchFirstName || '') + ' ' + (model.Integration.MerchLastName || '')}/>
                                </WrappedField>
                                <WrappedField label="Merchandiser title">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.MerchTitle || ''}/>
                                </WrappedField>
                                <WrappedField label="Merchandiser email">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.MerchEmail || ''}/>
                                </WrappedField>
                                <WrappedField label="Website">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.Website || ''}/>
                                </WrappedField>
                                <WrappedField label="Category" innerStyle={{gap: "0.5em"}}>
                                    {(model.Integration.Category || '').split('|').filter(r => !(!r)).map(r => <Chip key={r} label={r}/>)}
                                </WrappedField>
                                <WrappedField label="Subcategory">
                                    <TextField style={{flex: 1}} disabled value={model.Integration.Subcategory || ''}/>
                                </WrappedField>
                                {state.customFields.map(field => <WrappedField key={field.Id} label={field.Name}>
                                    <TextField style={{flex: 1}} disabled value={(model!.Integration[field.Id] as string) || ''}/>
                                </WrappedField>)}
                                <WrappedField label="Agreement signed by">
                                    <div style={{paddingTop: '4px'}}>
                                        {model.Integration.SignerName ? <>{model.Integration.SignerName} on <Moment format='MM/DD/YYYY [at] hh:mm A'>{model.Integration.SignDate}</Moment></> : ''}
                                    </div>
                                </WrappedField>
                            </AccordionDetails>
                        </Accordion>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={6} style={{display: 'flex'}}>
                    <RetailerOnboarding partner={(!Config.production || (Config.production && model.IsLive)) ? model.Integration.CoId : null} partnerName={model.Integration.CompanyName} tasks={tasks}/>
                </Grid>
                {model.Logs && model.Logs.length ?
                    <Grid size={12}>
                        <Typography variant='h6' style={{textAlign: 'left'}}>Events</Typography>
                        <Timeline style={{fontSize: '100%', margin: '0px 0px 0px 10px'}}>
                        {model.Logs.map(log =>
                            <Log key={log.Id} log={log}/>
                        )}
                        </Timeline>
                    </Grid>
                : ''}
            </Grid>
            <Dialog open={state.showReject} onClose={() => {mergeState({showReject: false})}} fullWidth maxWidth="xs">
                <Toolbar className='lbtoolbar'>Reject Integration</Toolbar>
                <Form onSubmit={reject}
                render={({ handleSubmit, pristine, invalid, values, form }) => (
                  <form autoComplete='off' onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', flex: 1}}>
                  <DialogContent>
                  <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                      <Field style={{width: '100%'}} validate={v => v ? undefined : 'Reason is required.'} label='Rejection reason' component={ExtendedField} name='RejectionReason'/>
                  </div>
                  </DialogContent>
                  <DialogActions>
                    <DownloadButton variant='text' color="primary" onClick={handleSubmit} type='submit' disabled={pristine || invalid}>Reject</DownloadButton>
                  </DialogActions>
                  </form>
            )}/>
            </Dialog>
            <Dialog open={state.showRejectGoLive} onClose={() => {mergeState({showRejectGoLive: false})}} fullWidth maxWidth="xs">
                <Toolbar className='lbtoolbar'>Reject Request To Go Live</Toolbar>
                <Form onSubmit={rejectGoLive}
                render={({ handleSubmit, pristine, invalid, values, form }) => (
                  <form autoComplete='off' onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', flex: 1}}>
                  <DialogContent>
                  <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                      <Field style={{width: '100%'}} validate={v => v ? undefined : 'Reason is required.'} label='Rejection reason' component={ExtendedField} name='RejectionReason'/>
                  </div>
                  </DialogContent>
                  <DialogActions>
                    <DownloadButton variant='text' color="primary" onClick={handleSubmit} type='submit' disabled={pristine || invalid}>Reject</DownloadButton>
                  </DialogActions>
                  </form>
            )}/>
            </Dialog>
            <Dialog open={state.showError} onClose={() => {mergeState({showError: false})}}>
                <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                <DialogContent><Typography>{state.error}</Typography></DialogContent>
            </Dialog>
            <ConfirmDialog open={state.showConfirm} onClose={() => mergeState({showConfirm: false})} message={state.confirmMessage} onConfirm={state.onConfirm}/>
            </>;
        }
        return <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '6.5em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '50em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '50em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                </Grid>
            </Grid>;
};

export default OnboardingDetails;
